import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useFormPersist — auto-saves a Formik form's values so a user never loses
 * what they typed on refresh or navigation, and restores it on the next visit.
 *
 * How it works:
 *   - On mount it reads any saved draft for `key` and merges it over the
 *     form's initialValues (so new fields still get their defaults).
 *   - On every change it writes the current values back to storage.
 *   - clearDraft() wipes the saved draft — call it after a successful submit
 *     or when the user explicitly discards.
 *
 * Storage defaults to sessionStorage (per-tab, cleared when the tab closes),
 * matching how this app already stores its auth token. Pass
 * `storage: window.localStorage` if you want the draft to survive a full
 * browser restart instead.
 *
 * @param {string} key           unique storage key for this form
 * @param {object} formik        the object returned by useFormik()
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]   turn persistence off (e.g. edit mode)
 * @param {Storage} [options.storage]        sessionStorage (default) | localStorage
 * @param {string[]} [options.exclude=[]]    value keys to never persist (secrets, files)
 * @param {*} [options.extra]                extra non-formik state to persist (e.g. wizard step)
 * @param {(saved:any)=>void} [options.onRestoreExtra]  restores `extra` on mount
 * @param {boolean} [options.forceSave=false]  save even when the form isn't dirty (pending changes kept outside formik)
 * @returns {{ clearDraft: () => void, restored: boolean, ready: boolean }}
 */
export default function useFormPersist(key, formik, options = {}) {
  const {
    enabled = true,
    storage = typeof window !== 'undefined' ? window.sessionStorage : undefined,
    exclude = [],
    extra,
    onRestoreExtra,
    forceSave = false,
  } = options;

  // Autosave must not run until we've finished (attempting) to hydrate,
  // otherwise the empty initial values would overwrite the saved draft on
  // the very first render.
  const [ready, setReady] = useState(false);
  const [restored, setRestored] = useState(false);

  // Keep the latest onRestoreExtra without making the mount effect re-run.
  const onRestoreExtraRef = useRef(onRestoreExtra);
  onRestoreExtraRef.current = onRestoreExtra;

  // ── hydrate once on mount ──────────────────────────────────────────────
  useEffect(() => {
    if (!enabled || !storage) {
      setReady(true);
      return;
    }
    try {
      const raw = storage.getItem(key);
      if (raw) {
        const saved = JSON.parse(raw);
        if (saved && saved.values && typeof saved.values === 'object') {
          formik.setValues({ ...formik.initialValues, ...saved.values }, false);
          setRestored(true);
        }
        if (onRestoreExtraRef.current && saved && saved.extra !== undefined) {
          onRestoreExtraRef.current(saved.extra);
        }
      }
    } catch {
      /* ignore corrupt/unreadable draft */
    }
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── autosave on change ─────────────────────────────────────────────────
  // Only persist once the user has actually changed something (formik.dirty).
  // Values that still match initialValues are just the untouched defaults —
  // saving those would create a draft out of a form nobody has filled in.
  useEffect(() => {
    if (!enabled || !ready || !storage) return;
    if (!formik.dirty && !forceSave) return;
    try {
      const v = { ...formik.values };
      exclude.forEach((f) => delete v[f]);
      storage.setItem(key, JSON.stringify({ values: v, extra, savedAt: Date.now() }));
    } catch {
      /* storage full or unavailable — non-fatal */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, enabled, formik.values, formik.dirty, forceSave, extra]);

  const clearDraft = useCallback(() => {
    try {
      storage?.removeItem(key);
    } catch {
      /* non-fatal */
    }
    setRestored(false);
  }, [key, storage]);

  return { clearDraft, restored, ready };
}
